import type { AgentV2ReferenceSections } from '@/agent-v2/references/agent-v2-reference-sections'
import {
  getAgentV2ReferenceDisplayName,
  getAgentV2ReferenceMetadataSummaryText,
  kAgentV2DocketQualifierLabel,
  kAgentV2LivingAssetKindLabels,
} from '@/agent-v2/utils/agent-v2-reference-displays'
import { copyWithSelection } from '@/agent-v2/utils/copy-plain-text'

/**
 * One copied references row: "[N] display name (qualifier)" plus the same one-line
 * metadata summary the on-screen references section shows.
 */
type CopiedReferenceRow = { label: string; metadata: string | null }
type CopiedReferenceSection = { title: string; rows: CopiedReferenceRow[] }

export async function copyAgentV2Response(responseElement: HTMLElement, referenceSections: AgentV2ReferenceSections): Promise<void> {
  // The references section is built from the same data the on-screen section derives
  // from (not the live DOM) so collapsed reference rows still copy.
  const copiedSections = buildCopiedReferenceSections(referenceSections)

  // Resolved legal citations render as prose-styled buttons without a visible number;
  // Append the footnote badge after authority text so the prose stays connected
  // to the numbered reference rows below.
  const chipLabelByCitationId = new Map<string, string>()
  for (const group of referenceSections.citedGroups) {
    for (const support of group.supports) chipLabelByCitationId.set(support.reference.citation_id, support.chipLabel)
  }
  // Legal-citation triggers are InlineTextButton spans (role=button), badges are
  // native buttons or external links, web citations are numberless links — flatten all to their text.
  const copiedElement = responseElement.cloneNode(true) as HTMLElement
  for (const button of copiedElement.querySelectorAll<HTMLElement>('button, [role="button"], a[data-citation-id], a.agent-v2-citation-marker')) {
    const chipLabel = button.dataset.citationId ? chipLabelByCitationId.get(button.dataset.citationId) : undefined
    const buttonText = button.textContent ?? ''
    button.replaceWith(document.createTextNode(chipLabel ? `${buttonText} [${chipLabel}]` : buttonText))
  }

  const responseText = readRenderedText(copiedElement).trim()
  if (!responseText) throw new Error('The response has no text to copy.')
  const plainText = [responseText, referenceSectionsToPlainText(copiedSections)].filter(Boolean).join('\n\n')

  const html = copiedElement.innerHTML + referenceSectionsToHtml(copiedSections)
  // Outlook owns the cross-origin frame and does not delegate clipboard-write.
  // Use selection copying before awaiting anything, preserving the user's activation.
  if (window.parent !== window && copyWithSelection(plainText, html)) return
  const clipboard = navigator.clipboard
  try {
    if (!clipboard) throw new Error('Clipboard access is unavailable.')
    if (typeof ClipboardItem !== 'undefined' && typeof clipboard.write === 'function') {
      try {
        await clipboard.write([
          new ClipboardItem({
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
            'text/html': new Blob([html], { type: 'text/html' }),
          }),
        ])
        return
      } catch {
        // Preserve the web plain-text fallback when rich copying is unavailable.
      }
    }
    await clipboard.writeText(plainText)
  } catch (error) {
    if (!copyWithSelection(plainText, html)) throw error
  }
}

// innerText preserves paragraph breaks only on rendered elements (textContent flattens
// them), so the modified clone is parked in the layout invisibly for one read.
function readRenderedText(element: HTMLElement): string {
  const host = document.createElement('div')
  host.style.cssText = 'position:fixed;left:0;top:0;opacity:0;pointer-events:none;z-index:-1'
  host.setAttribute('aria-hidden', 'true')
  host.appendChild(element)
  document.body.appendChild(host)
  try {
    return element.innerText || element.textContent || ''
  } finally {
    host.remove()
  }
}

function buildCopiedReferenceSections(referenceSections: AgentV2ReferenceSections): CopiedReferenceSection[] {
  return [
    { title: 'Cited References', groups: referenceSections.citedGroups },
    { title: 'Related References', groups: referenceSections.relatedGroups },
  ]
    .filter((section) => section.groups.length > 0)
    .map((section) => ({
      title: section.title,
      rows: section.groups.map((group) => {
        const reference = group.supports[0].reference
        const qualifier =
          reference.source_type === 'living_asset'
            ? kAgentV2LivingAssetKindLabels[reference.source.asset_kind]
            : reference.source_type === 'docket'
            ? kAgentV2DocketQualifierLabel
            : null
        const displayName = reference.source_type === 'web' ? `${reference.source.title} — ${reference.source.url}` : getAgentV2ReferenceDisplayName(reference)
        return {
          label: `[${group.footnoteNumber}] ${displayName}${qualifier ? ` (${qualifier})` : ''}`,
          metadata: getAgentV2ReferenceMetadataSummaryText(reference),
        }
      }),
    }))
}

function referenceSectionsToPlainText(sections: CopiedReferenceSection[]): string {
  return sections
    .map((section) => [section.title, ...section.rows.map((row) => (row.metadata ? `${row.label}\n${row.metadata}` : row.label))].join('\n'))
    .join('\n\n')
}

function referenceSectionsToHtml(sections: CopiedReferenceSection[]): string {
  if (sections.length === 0) return ''

  // Built through DOM nodes and text nodes so reference names never parse as markup.
  const container = document.createElement('div')
  for (const section of sections) {
    container.appendChild(document.createElement('hr'))
    const heading = document.createElement('h3')
    heading.textContent = section.title
    container.appendChild(heading)
    for (const row of section.rows) {
      const paragraph = document.createElement('p')
      paragraph.appendChild(document.createTextNode(row.label))
      if (row.metadata) {
        paragraph.appendChild(document.createElement('br'))
        paragraph.appendChild(document.createTextNode(row.metadata))
      }
      container.appendChild(paragraph)
    }
  }
  return container.innerHTML
}
